/**
 * Shared validation helpers for security hardening.
 *
 * Guards against malicious URL schemes in fetch calls, prototype pollution
 * in AI-generated JSON, and unbounded string lengths from LLM responses.
 *
 * @copyright VerdeKai 2025 - Proprietary and Confidential
 */

/** Maximum raw AI response length before we skip JSON regex extraction */
export const MAX_AI_RESPONSE_LENGTH = 50_000;

/**
 * Validates and normalizes an Ollama server URL.
 * Rejects non-HTTP(S) schemes (javascript:, file:, data:, etc.).
 *
 * @returns Normalized URL string (no trailing slash) or null if invalid
 */
export const validateOllamaUrl = (raw: string): string | null => {
  if (!raw || typeof raw !== 'string') return null;

  try {
    const url = new URL(raw.trim());

    // Only allow http and https schemes
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }

    // Strip trailing slash for consistent URL construction
    return url.origin + url.pathname.replace(/\/+$/, '');
  } catch {
    return null;
  }
};

/**
 * Validates a model name against a safe character set.
 * Ollama model names follow the pattern: name:tag or org/name:tag
 *
 * @returns true if the model name is safe to use
 */
export const isValidModelName = (name: string): boolean => {
  if (!name || typeof name !== 'string') return false;
  if (name.length > 128) return false;
  return /^[a-zA-Z0-9._:/-]+$/.test(name);
};

/**
 * Truncates a string to a maximum length.
 * Useful for bounding AI-generated text before storage or display.
 */
export const truncateString = (s: string, maxLen = 500): string => {
  if (typeof s !== 'string') return '';
  return s.length <= maxLen ? s : s.slice(0, maxLen);
};

/** Keys that enable prototype pollution when present in parsed JSON */
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Recursively strips prototype-pollution keys from a parsed JSON object.
 * Returns the cleaned value (mutates in place for performance).
 */
export const stripDangerousKeys = (obj: unknown): unknown => {
  if (obj === null || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      obj[i] = stripDangerousKeys(obj[i]);
    }
    return obj;
  }

  for (const key of Object.keys(obj as Record<string, unknown>)) {
    if (DANGEROUS_KEYS.has(key)) {
      delete (obj as Record<string, unknown>)[key];
    } else {
      (obj as Record<string, unknown>)[key] = stripDangerousKeys(
        (obj as Record<string, unknown>)[key]
      );
    }
  }

  return obj;
};
